/**
 * useFilterState Hook
 * Generic filter state management with active count, chips, and clear functionality
 */

import { useState, useEffect } from 'react';

export interface ActiveChip {
  key: string;
  label: string;
  value: string;
  onDelete: () => void;
}

interface UseFilterStateOptions<T> {
  onFilter?: (filters: T) => void;
  applyOnMount?: boolean;
  labels?: Partial<Record<keyof T, string>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatValue?: Partial<Record<keyof T, (value: any) => string>>;
  /**
   * When true, every setFilter applies immediately (calls onFilter with the new state).
   * Used by the inline filter layout so filters take effect without an "Aplicar" button.
   */
  live?: boolean;
}

export interface UseFilterStateReturn<T extends Record<string, unknown>> {
  filters: T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilter: (key: keyof T, value: any) => void;
  setFilters: (partial: Partial<T>) => void;
  clearAll: () => void;
  applyFilters: () => void;
  activeCount: number;
  activeChips: ActiveChip[];
  hasActiveFilters: boolean;
  isDefault: (key: keyof T) => boolean;
}

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
  }
  return false;
}

function formatDefaultValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFilterState<T extends Record<string, any>>(
  defaultValues: T,
  options?: UseFilterStateOptions<T>,
): UseFilterStateReturn<T> {
  const [filters, setFiltersState] = useState<T>(defaultValues);
  const onFilter = options?.onFilter;
  const applyOnMount = options?.applyOnMount;
  const labels = options?.labels;
  const formatValue = options?.formatValue;
  const live = options?.live;

  const [initialOnFilter] = useState(() => onFilter);
  useEffect(() => {
    if (applyOnMount) {
      initialOnFilter?.(defaultValues);
    }
  }, [applyOnMount, initialOnFilter, defaultValues]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setFilter = (key: keyof T, value: any) => {
    const next = { ...filters, [key]: value };
    setFiltersState(next);
    if (live) onFilter?.(next);
  };

  const setFilters = (partial: Partial<T>) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
  };

  const clearAll = () => {
    setFiltersState(defaultValues);
    onFilter?.(defaultValues);
  };

  const applyFilters = () => {
    onFilter?.(filters);
  };

  const isDefault = (key: keyof T) => isEqual(filters[key], defaultValues[key]);

  let activeCount = 0;
  const activeChips: ActiveChip[] = [];

  for (const key of Object.keys(defaultValues)) {
    if (key === 'searchTerm' || key === 'search') continue;
    if (isEqual(filters[key], defaultValues[key])) continue;

    activeCount++;

    // A yes/no filter already shows itself lit up in the bar; a chip would just
    // repeat the label with nothing after the colon.
    if (typeof filters[key] === 'boolean') continue;

    const label = labels?.[key as keyof T] ?? key;
    const formatter = formatValue?.[key as keyof T];
    const value = formatter ? formatter(filters[key]) : formatDefaultValue(filters[key]);

    activeChips.push({
      key,
      label: String(label),
      value,
      onDelete: () => {
        const updated = { ...filters, [key]: defaultValues[key] };
        setFiltersState(updated as T);
        onFilter?.(updated as T);
      },
    });
  }

  const hasActiveFilters = activeCount > 0;

  return {
    filters,
    setFilter,
    setFilters,
    clearAll,
    applyFilters,
    activeCount,
    activeChips,
    hasActiveFilters,
    isDefault,
  };
}
